import type { ApolloError } from '@apollo/client';
import type { FunctionComponent } from 'react';
import { Suspense, useEffect, useMemo, useRef } from 'react';

import { getApiGatewayUrl } from '@trello/api-gateway';
import { Analytics } from '@trello/atlassian-analytics';
import { ComponentWrapper } from '@trello/component-wrapper';
import {
  ChunkLoadErrorBoundary,
  ErrorBoundary,
} from '@trello/error-boundaries';
import { sendErrorEvent, sendNetworkErrorEvent } from '@trello/error-reporting';
import { getScreenFromUrl } from '@trello/marketing-screens';
import { Spinner } from '@trello/nachos/spinner';
import type { PIIString } from '@trello/privacy';
import { useLazyComponent } from '@trello/use-lazy-component';

import { useTrelloTemplateLanguagesQuery } from './TrelloTemplateLanguagesQuery.generated';

// eslint-disable-next-line @trello/less-matches-component
import styles from './Spinner.less';

const loadingSpinner = (
  <div className={styles.spinnerContainer}>
    <Spinner />
  </div>
);

const startTask = () => {
  return Analytics.startTask({
    taskName: 'view-templates',
    source: getScreenFromUrl(),
  });
};

const sendErrorEvents = (error: ApolloError, initialTaskId: string) => {
  const graphQLErrors = error?.graphQLErrors.toString();
  const url = getApiGatewayUrl('/graphql');

  if (error.networkError) {
    const statusString =
      error.networkError.toString().match(/status: ([0-9]+)/) || [];
    const status = statusString[1] ? parseInt(statusString[1], 10) : 504;
    sendNetworkErrorEvent({
      status,
      response: error.networkError.toString(),
      url,
      operationName: 'view-templates',
    });
  } else if (graphQLErrors) {
    sendNetworkErrorEvent({
      status: 200,
      response: graphQLErrors,
      url,
      operationName: 'view-templates',
    });
  } else {
    sendErrorEvent(new Error(error.message), {
      tags: {
        ownershipArea: 'trello-web-eng',
      },
      extraData: {
        networkError: false,
        extraInfo: error?.extraInfo,
        stack: error.stack ? error.stack : false,
        name: error.name,
      },
    });
  }

  Analytics.taskFailed({
    taskName: 'view-templates',
    traceId: initialTaskId,
    source: getScreenFromUrl(),
    error,
  });
};

export const LazyTemplatesContainer: FunctionComponent<{
  locale?: PIIString;
  category?: string;
  shortLink?: string;
  isInAppGallery: boolean;
}> = ({ locale, category, shortLink, isInAppGallery }) => {
  const TemplatesContainer = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "templates-container" */ './TemplatesContainer'
      ),
    {
      namedImport: 'TemplatesContainer',
      preload: true,
    },
  );

  const initialTaskId = useMemo(() => startTask(), []);
  const errorRef = useRef<boolean>(false);

  const getContext = () => ({
    traceId: initialTaskId,
  });

  const { data, error, loading } = useTrelloTemplateLanguagesQuery({
    waitOn: ['None'],
    context: getContext(),
  });

  useEffect(() => {
    if (data?.trello.templateLanguages) {
      Analytics.taskSucceeded({
        taskName: 'view-templates',
        traceId: initialTaskId,
        source: getScreenFromUrl(),
      });
    }
  }, [data?.trello.templateLanguages, initialTaskId]);

  useEffect(() => {
    // error handling
    if (errorRef.current) {
      return;
    }
    if (error) {
      sendErrorEvents(error, initialTaskId);
      errorRef.current = true;
    }
  }, [error, initialTaskId]);

  if (loading) {
    return loadingSpinner;
  } else if (data?.trello.templateLanguages) {
    return (
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-web-eng',
          feature: 'Templates Gallery',
        }}
      >
        <Suspense fallback={loadingSpinner}>
          <ChunkLoadErrorBoundary fallback={null}>
            <ComponentWrapper>
              <TemplatesContainer
                locale={locale}
                languages={data.trello.templateLanguages}
                category={category}
                shortLink={shortLink}
                isInAppGallery={isInAppGallery}
              />
            </ComponentWrapper>
          </ChunkLoadErrorBoundary>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return null;
};
